import {
  RequestsByIdsQuery,
  StoreRequestMetricsDocument,
  StoreRequestMetricsQuery,
  StoreRequestMetricsQueryVariables,
  useRequestsByIdsLazyQuery,
} from '@/buyers/_gen/gql'
import { QueryHookOptions, useQuery } from '@apollo/client'
import { useState } from 'react'
import chunk from 'lodash/chunk'
import uniq from 'lodash/uniq'

type StoreRequestMetric = StoreRequestMetricsQuery['storeRequestMetrics'][number]

type RequestForQuote = RequestsByIdsQuery['requestForQuotes'][number]

export interface StoreRequestMetricWithRfq extends StoreRequestMetric {
  requestForQuote: RequestForQuote
}

export type StoreRequestMetricsAggregated = {
  storeRequestMetrics: Array<StoreRequestMetricWithRfq>
}

function useStoreRequestMetricsPaginated(
  options: QueryHookOptions<StoreRequestMetricsQuery, StoreRequestMetricsQueryVariables> &
    ({ variables: StoreRequestMetricsQueryVariables; skip?: boolean } | { skip: boolean })
) {
  const [fetchRequestsByIds] = useRequestsByIdsLazyQuery({ client: options.client })
  const [prevData, setPrevData] = useState<StoreRequestMetricsAggregated>()
  const [data, setData] = useState<StoreRequestMetricsAggregated>()
  const [loadingRequests, setLoadingRequests] = useState<boolean>(false)

  const fetchAllRequests = async (metricsData: StoreRequestMetricsQuery) => {
    const requestIds = metricsData.storeRequestMetrics.map((m) => m.requestForQuoteId)
    const chunks = chunk(uniq(requestIds), 250)

    try {
      // Fetch all chunks in parallel, handling individual failures gracefully
      const results = await Promise.allSettled(
        chunks.map((chunkIds) => fetchRequestsByIds({ variables: { ids: chunkIds } }))
      )

      const requests: Record<string, RequestForQuote> = {}
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const pageResults = result.value.data?.requestForQuotes ?? []
          pageResults.forEach((rfq) => {
            requests[rfq.id] = rfq
          })
        }
      })

      const storeRequestMetrics = metricsData.storeRequestMetrics.map((m) => ({
        ...m,
        requestForQuote: requests[m.requestForQuoteId],
      }))

      setData({ ...metricsData, storeRequestMetrics })
    } finally {
      setLoadingRequests(false)
    }
  }

  const {
    refetch,
    loading: loadingBaseMetrics,
    error,
  } = useQuery<StoreRequestMetricsQuery, StoreRequestMetricsQueryVariables>(
    StoreRequestMetricsDocument,
    {
      ...options,
      onCompleted(metricsData) {
        setPrevData(data)
        setData(undefined)
        setLoadingRequests(true)

        fetchAllRequests(metricsData)
      },
    }
  )

  return { data, prevData, loading: loadingBaseMetrics || loadingRequests, refetch, error }
}

export default useStoreRequestMetricsPaginated
